import { Link } from "@inertiajs/react"
import { Building2, Search, User } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Icon } from "@/components/icon"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { useDebounce } from "@/hooks/use-debounce"
import { companyPath, rankingPath } from "@/routes"

interface SearchResult {
  id: number
  name: string
  type: "company" | "developer"
  company_name?: string
}

interface SearchResponse {
  companies: SearchResult[]
  developers: SearchResult[]
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResponse>({
    companies: [],
    developers: [],
  })
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const debouncedQuery = useDebounce(query, 300)

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])

  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
    } else {
      setQuery("")
      setResults({ companies: [], developers: [] })
    }
  }, [open])

  useEffect(() => {
    if (debouncedQuery.length === 0) {
      setResults({ companies: [], developers: [] })
      return
    }

    const fetchResults = async () => {
      setLoading(true)
      try {
        const response = await fetch(
          `/search?q=${encodeURIComponent(debouncedQuery)}`,
        )
        const data = (await response.json()) as SearchResponse
        setResults(data)
      } catch (error) {
        console.error("Search failed:", error)
      } finally {
        setLoading(false)
      }
    }

    void fetchResults()
  }, [debouncedQuery])

  const handleSelect = () => {
    setOpen(false)
  }

  const hasResults =
    results.companies.length > 0 || results.developers.length > 0

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="group h-9 w-9 cursor-pointer"
        onClick={() => setOpen(true)}
      >
        <Search className="!size-5 opacity-80 group-hover:opacity-100" />
        <span className="sr-only">Search</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg p-0" showCloseButton={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              ref={inputRef}
              placeholder="Search companies or developers..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading && (
              <div className="text-muted-foreground p-4 text-center text-sm">
                Searching...
              </div>
            )}
            {!loading && !hasResults && query.length > 0 && (
              <div className="text-muted-foreground p-4 text-center text-sm">
                No results found for &ldquo;{query}&rdquo;
              </div>
            )}
            {!loading && query.length === 0 && (
              <div className="text-muted-foreground p-4 text-center text-sm">
                Start typing to search
              </div>
            )}
            {!loading && hasResults && (
              <div className="p-2">
                {results.companies.length > 0 && (
                  <div className="mb-2">
                    <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
                      Companies
                    </div>
                    {results.companies.map((company) => (
                      <Link
                        key={company.id}
                        href={companyPath(company.id)}
                        onClick={handleSelect}
                        className="hover:bg-accent flex items-center gap-2 rounded-md px-2 py-1.5 text-sm"
                      >
                        <Icon
                          iconNode={Building2}
                          className="text-muted-foreground h-4 w-4"
                        />
                        <span>{company.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
                {results.developers.length > 0 && (
                  <div>
                    <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
                      Developers
                    </div>
                    {results.developers.map((developer) => (
                      <Link
                        key={developer.id}
                        href={rankingPath(developer.id)}
                        onClick={handleSelect}
                        className="hover:bg-accent flex items-center gap-2 rounded-md px-2 py-1.5 text-sm"
                      >
                        <Icon
                          iconNode={User}
                          className="text-muted-foreground h-4 w-4"
                        />
                        <span>{developer.name}</span>
                        {developer.company_name && (
                          <span className="text-muted-foreground">
                            · {developer.company_name}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="text-muted-foreground border-t px-3 py-2 text-xs">
            <kbd className="bg-muted pointer-events-none inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium select-none">
              <span className="text-xs">⌘</span>K
            </kbd>{" "}
            to open search
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
